/**
 * Cookie consent — cookie-backed, Alpine-native
 *
 * Register:
 *   import cookieConsent from "./cookieConsent.js";
 *   Alpine.data("cookieConsent", cookieConsent);
 *
 * Expects dormant analytics scripts:
 *   <script type="text/plain" data-consent="analytics" data-src="..." data-async></script>
 *   <script type="text/plain" data-consent="analytics"> ... </script>
 */

/**
 * Helpers
 */
const prefersPrivacy = () => {
  return (
    navigator?.globalPrivacyControl === true ||
    navigator?.doNotTrack === "1" ||
    window?.doNotTrack === "1" ||
    navigator?.msDoNotTrack === "1"
  );
};

const activateConsentScripts = (category) => {
  const selector = `script[type="text/plain"][data-consent="${category}"]:not([data-activated])`;
  document.querySelectorAll(selector).forEach((oldScript) => {
    const script = document.createElement("script");

    const src = oldScript.dataset.src;
    if (src) {
      script.src = src;
      if (oldScript.hasAttribute("data-async")) script.async = true;
    } else {
      script.textContent = oldScript.textContent || "";
    }

    if (oldScript.id) script.id = oldScript.id;

    oldScript.setAttribute("data-activated", "true");
    oldScript.replaceWith(script);
  });
};

const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const parts = document.cookie.split(";");

  for (let i = 0; i < parts.length; i++) {
    const c = parts[i].trim();
    if (c.indexOf(nameEQ) === 0) {
      return decodeURIComponent(c.substring(nameEQ.length));
    }
  }

  return null;
};

const setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  const secure = location.protocol === "https:" ? ";Secure" : "";
  document.cookie =
    `${name}=${encodeURIComponent(value)};` +
    `${expires};path=/;SameSite=Lax${secure}`;
};

const removeCookie = (name) => {
  const secure = location.protocol === "https:" ? ";Secure" : "";
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax${secure}`;
};

export default function cookieConsent(config = {}) {
  const cookieName = config.cookieName || "cookieConsent";
  const cookieLifetimeDays = Number.isFinite(config.cookieLifetimeDays)
    ? config.cookieLifetimeDays
    : 180;
  const consentRequired = config.consentRequired ?? true;

  return {
    // Source of truth for UI + behaviour
    consent: null, // null | "all" | "required"

    // Derived UI state
    showBanner: false,

    init() {
      // Hydrate from cookie once on init
      const existing = getCookie(cookieName);

      if (existing === "all" || existing === "required") {
        // 1. Existing choice always wins
        this.consent = existing;
      } else if (prefersPrivacy()) {
        // 2. Privacy signal always opts out (no banner)
        this.consent = "required";
      } else {
        // 3. New user: Default based on geography (passed from Twig)
        this.consent = consentRequired ? null : 'all';
      }

      // Banner only shows if consent is still null
      this.showBanner = this.consent === null;

      // Activate scripts immediately if the final result is 'all'
      if (this.consent === "all") {
        activateConsentScripts("analytics");
      }

      // Keep cookie in sync with consent changes (Alpine-native, auto-cleaned on destroy)
      this.$watch("consent", (value) => {
        if (value === "all" || value === "required") {
          setCookie(cookieName, value, cookieLifetimeDays);
        } else {
          removeCookie(cookieName);
        }
      });
    },

    acceptAll() {
      this.consent = "all";
      this.showBanner = false;
      activateConsentScripts("analytics");
    },

    allowRequiredOnly() {
      this.consent = "required";
      this.showBanner = false;
    },

    reopen() {
      this.consent = null;
      this.showBanner = true;
    },
  };
}
